/**
 * Phase 8 base AC18 — Per-visit document upload with PI relevance.
 *
 * Coordinator (or above) picks a file → BE mints a presigned S3 PUT
 * URL → FE uploads → BE finalises the row and mirrors it into the
 * eISF binder (log-and-continue on mirror failure). The document
 * list below shows every attached file with a per-row PI relevance
 * toggle (Yes / No / unset) — visible to all roles, editable by the
 * PI (or sub-investigator / platform admin).
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  visitDocuments,
  type PiRelevance,
  type VisitDocumentRow,
} from '@/api/client';
import {
  CheckCircle2,
  CloudUpload,
  FileText,
  Loader2,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import { useHasRole } from '@/components/RoleGate';
import { useAuth } from '@/contexts/AuthContext';
import { fmtDateTime } from '@/lib/dates';
import { errorText } from '@/lib/permissionError';
// Shared helper: it echoes the x-amz-server-side-encryption header the BE
// signs into the presigned URL. The local copy this replaces didn't, so
// every visit document upload failed with S3 SignatureDoesNotMatch.
import { uploadWithProgress } from '@/lib/s3Upload';

const PI_ROLES = [
  'platform_admin',
  'application_admin',
  'principal_investigator',
  'sub_investigator',
] as const;

const RELEVANCE_LABEL: Record<PiRelevance, string> = {
  unset: 'Relevance unset',
  yes: 'Relevant',
  no: 'Not relevant',
};

const RELEVANCE_BADGE: Record<PiRelevance, string> = {
  unset: 'bg-slate-100 text-slate-700 border-slate-200',
  yes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  no: 'bg-amber-50 text-amber-700 border-amber-200',
};

const RELEVANCE_ICON: Record<PiRelevance, typeof CheckCircle2> = {
  unset: MinusCircle,
  yes: CheckCircle2,
  no: XCircle,
};

const ACCEPT =
  '.pdf,.docx,.png,.jpg,.jpeg,.tif,.tiff,.xls,.xlsx,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/tiff,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

const MAX_SIZE_MB = 200;

export function VisitDocumentsPanel({ visitId }: { visitId: string }) {
  const canMarkRelevance = useHasRole(PI_ROLES);
  const { user } = useAuth();
  const isMonitor = user?.role === 'monitor';
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const q = useQuery({
    queryKey: ['visit', 'documents', visitId],
    queryFn: () => visitDocuments.list(visitId),
    enabled: !!visitId,
  });

  const rows = q.data?.rows ?? [];

  async function handleFile(file: File) {
    if (uploading) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large — max ${MAX_SIZE_MB} MB.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const presigned = await visitDocuments.presign(visitId, {
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_size: file.size,
      });
      await uploadWithProgress(presigned.upload_url, file, file.type || 'application/octet-stream', setProgress);
      const result = await visitDocuments.finalise(visitId, {
        visit_document_id: presigned.visit_document_id,
        s3_bucket: presigned.s3_bucket,
        s3_key: presigned.s3_key,
        file_name: presigned.file_name,
        mime_type: file.type || 'application/octet-stream',
        file_size: file.size,
      });
      if (result.mirror_result) {
        toast.success(`Uploaded ${result.reference_number} and mirrored to eISF.`);
      } else {
        toast.success(
          `Uploaded ${result.reference_number}. eISF mirror queued — will retry.`,
        );
      }
      qc.invalidateQueries({ queryKey: ['visit', 'documents', visitId] });
    } catch (err) {
      toast.error(errorText(err, 'Upload failed'));
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <section className="card p-4">
      <header className="mb-3 flex items-start justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <FileText className="h-4 w-4 text-brand-500" />
            Visit documents
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-muted">
            Attachments captured against this visit. Mirrored to the eISF
            binder automatically.
          </p>
        </div>
        {!isMonitor && (
          <div>
            <label
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 ${
                uploading ? 'pointer-events-none opacity-60' : ''
              }`}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading {progress}%
                </>
              ) : (
                <>
                  <CloudUpload className="h-3 w-3" />
                  Upload document
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                }}
              />
            </label>
          </div>
        )}
      </header>

      {q.isLoading ? (
        <div className="text-[11px] text-ink-muted">
          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded border border-dashed border-surface-border p-4 text-center text-[11px] text-ink-muted">
          No documents attached to this visit yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((doc) => (
            <DocumentRow
              key={doc.visit_document_id}
              doc={doc}
              canMarkRelevance={canMarkRelevance}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function DocumentRow({
  doc,
  canMarkRelevance,
}: {
  doc: VisitDocumentRow;
  canMarkRelevance: boolean;
}) {
  const qc = useQueryClient();
  const [showNote, setShowNote] = useState(false);
  const [pendingRelevance, setPendingRelevance] = useState<PiRelevance | null>(null);
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: (body: { pi_relevance: PiRelevance; pi_relevance_note?: string }) =>
      visitDocuments.setRelevance(doc.visit_document_id, body),
    onSuccess: () => {
      toast.success('Relevance updated.');
      setShowNote(false);
      setPendingRelevance(null);
      setNote('');
      qc.invalidateQueries({ queryKey: ['visit', 'documents', doc.visit_id] });
    },
    onError: (e) => toast.error(errorText(e, 'Update failed')),
  });

  const Icon = RELEVANCE_ICON[doc.pi_relevance];
  const sizeLabel = useMemo(() => humanSize(doc.file_size), [doc.file_size]);

  return (
    <li className="rounded border border-surface-border bg-surface-card p-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-ink-secondary">
              {doc.reference_number}
            </span>
            <span className="truncate font-medium text-ink-primary" title={doc.file_name}>
              {doc.file_name}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-ink-muted">
            <span>{doc.mime_type}</span>
            <span>·</span>
            <span>{sizeLabel}</span>
            <span>·</span>
            <span>uploaded {fmtDateTime(doc.uploaded_at)}</span>
          </div>
          {doc.pi_relevance_note && (
            <div className="mt-1 rounded bg-surface-muted/40 p-1.5 text-[11px] text-ink-secondary">
              <span className="font-semibold">PI note:</span> {doc.pi_relevance_note}
            </div>
          )}
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${RELEVANCE_BADGE[doc.pi_relevance]}`}
          title="PI relevance"
        >
          <Icon className="h-2.5 w-2.5" />
          {RELEVANCE_LABEL[doc.pi_relevance]}
        </span>
      </div>

      {canMarkRelevance && (
        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-surface-border pt-2">
          {(['yes', 'no', 'unset'] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                if (r === 'yes' || r === 'no') {
                  setPendingRelevance(r);
                  setShowNote(true);
                } else {
                  mut.mutate({ pi_relevance: 'unset' });
                }
              }}
              disabled={mut.isPending || doc.pi_relevance === r}
              className={`rounded border px-2 py-0.5 text-[10px] font-medium hover:bg-surface-muted/50 disabled:opacity-40 ${
                doc.pi_relevance === r
                  ? 'border-accent-400 bg-accent-50/40 text-accent-700'
                  : 'border-surface-border'
              }`}
            >
              Mark {RELEVANCE_LABEL[r].toLowerCase()}
            </button>
          ))}
        </div>
      )}

      {showNote && pendingRelevance && (
        <div className="mt-2 rounded border border-accent-200 bg-accent-50/30 p-2">
          <label className="block text-[10px] font-medium text-ink-secondary">
            Optional note for "{RELEVANCE_LABEL[pendingRelevance]}"
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why is this document relevant / not relevant?"
              className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-[11px]"
            />
          </label>
          <div className="mt-1.5 flex justify-end gap-1">
            <button
              type="button"
              onClick={() => {
                setShowNote(false);
                setPendingRelevance(null);
                setNote('');
              }}
              className="rounded border border-surface-border px-2 py-0.5 text-[10px] font-medium hover:bg-surface-muted/50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                mut.mutate({
                  pi_relevance: pendingRelevance,
                  pi_relevance_note: note.trim() || undefined,
                })
              }
              disabled={mut.isPending}
              className="rounded bg-accent-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-accent-700 disabled:opacity-50"
            >
              {mut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
